import { FC } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { CampaignDetailRow } from '../threeMonth.types';
import { formatCurrency, formatPercent } from '../utils/forecastingAggregations';
import { ChartTitle, TablePanel } from '../Forecasting.styles';
import { FORECASTING_TABLE_CSS } from '../forecastingTableStyles';
import { pipedriveDealUrl } from '@/utils';

interface CampaignDetailTableProps {
  data: CampaignDetailRow[];
}

function currencyCell(value: number) {
  return <span className="forecasting-currency-cell">{formatCurrency(value)}</span>;
}

export const CampaignDetailTable: FC<CampaignDetailTableProps> = ({ data }) => {
  const columns: ColumnsType<CampaignDetailRow> = [
    {
      title: 'ID',
      dataIndex: 'campaignId',
      key: 'campaignId',
      width: 72,
      /* The campaign id is the PipeDrive deal id, so it opens the deal — in a
         new tab, so the dashboard keeps its filters and drill-down. It was
         coloured like a link without being one. Only a numeric id links: the
         sheet fills a missing one with a dash, which is not a deal. */
      render: (v: string) => {
        const url = /^\d+$/.test(String(v).trim()) ? pipedriveDealUrl(v) : undefined;
        if (!url) return v;
        return (
          <a href={url} target="_blank" rel="noreferrer noopener" style={{ color: '#5b8def' }}>
            {v}
          </a>
        );
      }
    },
    { title: 'Campaign Name', dataIndex: 'campaignName', key: 'campaignName', ellipsis: true, width: 140 },
    { title: 'Buying Point', dataIndex: 'buyingPoint', key: 'buyingPoint', ellipsis: true, width: 100 },
    { title: 'Advertiser', dataIndex: 'advertiser', key: 'advertiser', ellipsis: true, width: 100 },
    { title: 'Status', dataIndex: 'status', key: 'status', ellipsis: true, width: 90 },
    { title: 'Start Date', dataIndex: 'startDate', key: 'startDate', width: 88 },
    { title: 'End Date', dataIndex: 'endDate', key: 'endDate', width: 88 },
    {
      title: 'Booked',
      dataIndex: 'booked',
      key: 'booked',
      align: 'right',
      width: 100,
      render: (v: number) => currencyCell(v)
    },
    {
      title: 'Pipe',
      dataIndex: 'pipe',
      key: 'pipe',
      align: 'right',
      width: 100,
      render: (v: number) => currencyCell(v)
    },
    {
      title: 'Pre-Pipe',
      dataIndex: 'prePipe',
      key: 'prePipe',
      align: 'right',
      width: 100,
      render: (v: number) => currencyCell(v)
    },
    {
      title: 'GP',
      dataIndex: 'gp',
      key: 'gp',
      align: 'right',
      width: 100,
      render: (v: number) => currencyCell(v)
    },
    {
      title: 'Margin',
      dataIndex: 'margin',
      key: 'margin',
      align: 'right',
      render: (v: number) => formatPercent(v, 1)
    }
  ];

  const totals = data.reduce(
    (acc, row) => ({
      booked: acc.booked + row.booked,
      pipe: acc.pipe + row.pipe,
      prePipe: acc.prePipe + row.prePipe,
      gp: acc.gp + row.gp
    }),
    { booked: 0, pipe: 0, prePipe: 0, gp: 0 }
  );
  const totalRev = totals.booked + totals.pipe + totals.prePipe;

  return (
    <TablePanel>
      <ChartTitle>Campaign detail</ChartTitle>
      <Table<CampaignDetailRow>
        size="small"
        pagination={{ pageSize: 15, size: 'small', showSizeChanger: false }}
        rowKey="key"
        columns={columns}
        dataSource={data}
        scroll={{ x: 1200, y: 280 }}
        className="forecasting-bp-table"
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={7}>
                Grand total
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">
                {currencyCell(totals.booked)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} align="right">
                {currencyCell(totals.pipe)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={9} align="right">
                {currencyCell(totals.prePipe)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10} align="right">
                {currencyCell(totals.gp)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={11} align="right">
                {formatPercent(totalRev > 0 ? (totals.gp / totalRev) * 100 : 0, 1)}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      <style>{FORECASTING_TABLE_CSS}</style>
    </TablePanel>
  );
};
